import * as Y from "yjs";
import {
	PREFERRED_TRIM_SIZE,
	SHARED_YJS_DB_NAME,
	clearAllLocalDocuments,
	clearDocument,
	customStoreName,
	fetchUpdates,
	getScopedCustomKey,
	hasRequiredStores,
	invalidateSharedDB,
	openRawDB,
	registerPersistenceInstance,
	resetSharedDatabase,
	rtop,
	storeState,
	unregisterPersistenceInstance,
	updatesStoreName,
} from "./indexeddb-store-operations";

export {
	PREFERRED_TRIM_SIZE,
	SHARED_YJS_DB_NAME,
	clearAllLocalDocuments,
	clearDocument,
	fetchUpdates,
	storeState,
};

type SyncedListener = (persistence: IndexeddbPersistence) => void;

/**
 * Resilient IndexedDB persistence provider for Y.Doc backed by a shared
 * IndexedDB database (`illumi-local`).
 *
 * Multiplexes all boards and cards onto a single shared `IDBDatabase`
 * connection to avoid opening hundreds of per-card databases simultaneously,
 * while protecting against:
 * - Browser-closed IDBDatabase connections after tab/OS sleep (`InvalidStateError`)
 * - Missing object stores from interrupted upgrades (`NotFoundError`)
 * - Synchronous exceptions in `Y.Doc.on('update')` aborting subsequent observers
 * - Unhandled promise rejections when `destroy()` races with in-flight DB open
 */
export class IndexeddbPersistence {
	readonly doc: Y.Doc;
	readonly name: string;
	_dbref = 0;
	_dbsize = 0;
	_destroyed = false;
	db: IDBDatabase | null = null;
	synced = false;
	_db: Promise<IDBDatabase | null>;
	readonly whenSynced: Promise<IndexeddbPersistence>;
	_storeTimeout = 1000;
	_storeTimeoutId: ReturnType<typeof setTimeout> | null = null;
	readonly _storeUpdate: (update: Uint8Array, origin: unknown) => void;

	private _listeners = new Map<string, Set<SyncedListener>>();
	private _reconnectPromise: Promise<IDBDatabase | null> | null = null;
	private _hasAttemptedSchemaRecovery = false;

	constructor(name: string, doc: Y.Doc) {
		this.doc = doc;
		this.name = name;
		registerPersistenceInstance(this);

		this.whenSynced = new Promise<IndexeddbPersistence>((resolve) => {
			this.on("synced", () => resolve(this));
		});

		this._storeUpdate = (update: Uint8Array, origin: unknown) => {
			if (this._destroyed || origin === this) {
				return;
			}
			if (!this.db) {
				this._scheduleReconnect();
				return;
			}
			try {
				const tx = this.db.transaction([updatesStoreName], "readwrite");
				const updatesStore = tx.objectStore(updatesStoreName);
				rtop(updatesStore.add({ doc: this.name, update })).catch((err) => {
					this._handleDbError(err);
				});
				if (++this._dbsize >= PREFERRED_TRIM_SIZE) {
					if (this._storeTimeoutId !== null) {
						clearTimeout(this._storeTimeoutId);
					}
					this._storeTimeoutId = setTimeout(() => {
						this._storeTimeoutId = null;
						if (!this._destroyed && this.db) {
							void storeState(this, false);
						}
					}, this._storeTimeout);
				}
			} catch (err) {
				this._handleDbError(err);
			}
		};

		this._reconnectPromise = this._connectAndHydrate().finally(() => {
			this._reconnectPromise = null;
		});
		this._db = this._reconnectPromise;
		doc.on("update", this._storeUpdate);
		this.destroy = this.destroy.bind(this);
		doc.on("destroy", this.destroy);
	}

	on(event: "synced", listener: SyncedListener): void {
		let set = this._listeners.get(event);
		if (!set) {
			set = new Set();
			this._listeners.set(event, set);
		}
		set.add(listener);
	}

	off(event: "synced", listener: SyncedListener): void {
		this._listeners.get(event)?.delete(listener);
	}

	emit(event: "synced", args: [IndexeddbPersistence]): void {
		const set = this._listeners.get(event);
		if (!set) return;
		for (const listener of set) {
			listener(...args);
		}
	}

	private _openVerifiedDB(): Promise<IDBDatabase | null> {
		return openRawDB(SHARED_YJS_DB_NAME)
			.then((db): Promise<IDBDatabase | null> | IDBDatabase | null => {
				if (this._destroyed) {
					return null;
				}
				if (!hasRequiredStores(db)) {
					if (!this._hasAttemptedSchemaRecovery) {
						this._hasAttemptedSchemaRecovery = true;
						this._dbref = 0;
						this._dbsize = 0;
						return resetSharedDatabase().then(() => this._openVerifiedDB());
					}
					invalidateSharedDB(db);
					return null;
				}
				return db;
			})
			.catch(() => null);
	}

	private _connectAndHydrate(): Promise<IDBDatabase | null> {
		return this._openVerifiedDB().then((db) => {
			if (!db || this._destroyed) {
				return null;
			}

			this.db = db;

			const beforeApplyUpdatesCallback = (updatesStore: IDBObjectStore) => {
				rtop(
					updatesStore.add({
						doc: this.name,
						update: Y.encodeStateAsUpdate(this.doc),
					}),
				).catch((err) => {
					this._handleDbError(err);
				});
			};

			const afterApplyUpdatesCallback = () => {
				if (this._destroyed) return;
				if (!this.synced) {
					this.synced = true;
					this.emit("synced", [this]);
				}
			};

			return fetchUpdates(
				this,
				beforeApplyUpdatesCallback,
				afterApplyUpdatesCallback,
			).then(() => this.db);
		});
	}

	_handleDbError(err: unknown): void {
		if (this._destroyed) {
			return;
		}
		const errorName = err instanceof Error ? err.name : "";
		const currentDb = this.db;
		this.db = null;
		invalidateSharedDB(currentDb);

		if (errorName === "NotFoundError" && !this._hasAttemptedSchemaRecovery) {
			this._hasAttemptedSchemaRecovery = true;
			this._dbref = 0;
			this._dbsize = 0;
			this._reconnectPromise = resetSharedDatabase()
				.catch(() => undefined)
				.then(() => this._connectAndHydrate())
				.finally(() => {
					this._reconnectPromise = null;
				});
			this._db = this._reconnectPromise;
			return;
		}

		this._scheduleReconnect();
	}

	private _scheduleReconnect(): void {
		if (this._destroyed || this._reconnectPromise) {
			return;
		}
		this._reconnectPromise = this._connectAndHydrate().finally(() => {
			this._reconnectPromise = null;
		});
		this._db = this._reconnectPromise;
	}

	destroy(): Promise<void> {
		if (this._storeTimeoutId !== null) {
			clearTimeout(this._storeTimeoutId);
			this._storeTimeoutId = null;
		}
		this.doc.off("update", this._storeUpdate);
		this.doc.off("destroy", this.destroy);
		this._destroyed = true;
		this._listeners.clear();
		this.db = null;
		unregisterPersistenceInstance(this);

		return this._db.then(() => undefined).catch(() => undefined);
	}

	clearData(): Promise<void> {
		return clearDocument(this.name).then(() => this.destroy());
	}

	get(key: IDBValidKey): Promise<unknown> {
		return this._db.then((db) => {
			if (!db || this._destroyed) return undefined;
			const tx = db.transaction([customStoreName], "readonly");
			const custom = tx.objectStore(customStoreName);
			return rtop(custom.get(getScopedCustomKey(this.name, key)));
		});
	}

	set(key: IDBValidKey, value: unknown): Promise<IDBValidKey | undefined> {
		return this._db.then((db) => {
			if (!db || this._destroyed) return undefined;
			const tx = db.transaction([customStoreName], "readwrite");
			const custom = tx.objectStore(customStoreName);
			return rtop(custom.put(value, getScopedCustomKey(this.name, key)));
		});
	}

	del(key: IDBValidKey): Promise<void> {
		return this._db.then((db) => {
			if (!db || this._destroyed) return;
			const tx = db.transaction([customStoreName], "readwrite");
			const custom = tx.objectStore(customStoreName);
			return rtop(custom.delete(getScopedCustomKey(this.name, key))).then(
				() => undefined,
			);
		});
	}
}
